import { byFieldDesc, sort } from "@punks/core"
import { NextRouter } from "next/router"
import {
  AvailabilitySearchResultBrand,
  AvailabilitySearchResultCategory,
  AvailabilitySearchResultsAggregations,
  AvailabilitySearchResultTag,
  AvailabilitySearchResultTagCategory,
} from "../../../api/backend/backend-api"
import { categoryPagePath } from "../../../root/routing/urls"
import {
  SearchFilterCategory,
  SearchFilterOption,
  SearchFilters,
} from "../types"
import { AvailabilitySearchFilters } from "../../../containers/serp/types"

const toDefaultCategory = (
  tagCategory: AvailabilitySearchResultTagCategory
): SearchFilterCategory => ({
  key: tagCategory.key,
  categoryName: tagCategory.categoryName,
  weight: tagCategory.weight,
  options: tagCategory.tags,
  active: tagCategory.active,
})

const toCategoryOption = (
  tag: AvailabilitySearchResultTag
): SearchFilterOption => {
  return {
    code: tag.code,
    name: tag.name,
    occurrences: tag.occurrences,
    value: tag.value,
  }
}

const toCategoryCategory = (
  tagCategory: AvailabilitySearchResultTagCategory
): SearchFilterCategory => {
  return {
    key: tagCategory.key,
    categoryName: tagCategory.categoryName,
    weight: tagCategory.weight,
    options: tagCategory.tags.map((x) => toCategoryOption(x)),
    active: tagCategory.active,
  }
}

const toCategory = (
  tagCategory: AvailabilitySearchResultTagCategory
): SearchFilterCategory => {
  if (tagCategory.key === "category") {
    return toCategoryCategory(tagCategory)
  }
  return toDefaultCategory(tagCategory)
}

const toBrandCategory = (
  brands: AvailabilitySearchResultBrand[]
): SearchFilterCategory => ({
  key: "brand",
  categoryName: "Brand",
  weight: 150,
  options: brands.map((x) => ({
    code: x.code,
    name: x.name,
    occurrences: x.occurrences,
    value: `brand:${x.code}`,
  })),
  active: true,
})

const buildPath = (
  category: AvailabilitySearchResultCategory,
  parents?: AvailabilitySearchResultCategory[]
) => categoryPagePath([...(parents?.map((x) => x.slug) ?? []), category.slug])

const toProductCategoryOption = (
  router: NextRouter,
  category: AvailabilitySearchResultCategory,
  parents?: AvailabilitySearchResultCategory[]
): SearchFilterOption => ({
  code: category.code,
  name: category.name,
  value: `category:${category.code}`,
  occurrences: category.occurrences,
  children:
    category.children?.length > 0
      ? category.children.map((x) =>
          toProductCategoryOption(router, x as any, [
            ...(parents ?? []),
            category,
          ])
        )
      : [],
  customAction: category.externalCategory
    ? () => {
        router.push(buildPath(category, parents))
      }
    : undefined,
})

const toProductCategory = (
  router: NextRouter,
  categories: AvailabilitySearchResultCategory[]
): SearchFilterCategory => {
  return {
    key: "category",
    categoryName: "Categoria",
    weight: 1000,
    options: categories.map((x) => toProductCategoryOption(router, x)),
    active: true,
  }
}

const isBooleanFilterCategory = (category: SearchFilterCategory) => {
  return (
    category.options[0].code?.toLowerCase() === "true" ||
    category.options[0].code?.toLowerCase() === "false"
  )
}

const isSwitchFilterToExclude = (
  category: SearchFilterCategory,
  initialFilters?: AvailabilitySearchFilters
) => {
  if (!isBooleanFilterCategory(category)) {
    return false
  }

  return initialFilters?.tags?.some(
    (x: any) => category.key.toLowerCase() === x.key.toLowerCase()
  )
}

export const toSearchFilters = (
  router: NextRouter,
  aggregations: AvailabilitySearchResultsAggregations,
  initialFilters?: AvailabilitySearchFilters
): SearchFilters => {
  return {
    categories: sort(
      [
        toProductCategory(router, aggregations.productCategories),
        ...aggregations.productTags
          .filter((x) => x.key !== "category")
          .map((x) => toCategory(x))
          .filter((x) => !isSwitchFilterToExclude(x, initialFilters)),
        ...(aggregations.brands.length > 1
          ? [toBrandCategory(aggregations.brands)]
          : []),
      ],
      byFieldDesc((x) => x.weight)
    ),
    prices: {
      min: aggregations.prices.min ?? undefined,
      max: aggregations.prices.max ?? undefined,
    },
    discountPercent: {
      min: aggregations.discountPercent.min ?? undefined,
      max: aggregations.discountPercent.max ?? undefined,
      values: aggregations.discountPercent.values.map((x) => ({
        value: x.value,
        occurrences: x.occurrences,
      })),
    },
  }
}

export const mergeFilters = (
  filters: AvailabilitySearchFilters,
  initialFilters?: AvailabilitySearchFilters
): AvailabilitySearchFilters => {
  return {
    ...filters,
    brandCodes:
      (filters.brandCodes?.length ?? 0) > 0
        ? filters.brandCodes
        : initialFilters?.brandCodes,
    tags: [...(filters.tags ?? []), ...(initialFilters?.tags ?? [])],
    minDiscountPercent: initialFilters?.minDiscountPercent,
    maxDiscountPercent: initialFilters?.maxDiscountPercent,
  }
}
